import type { LogDetail } from "../../../services/event-log";
import type { ScheduledSpeech } from "../../../services/speech";
import { scheduleNodeId, type DeliveryState } from "./model";

/**
 * The moments of a session, as Ruling 84 of 16 October 2026 (a) names them.
 *
 * > "The bug report records the whole session, with times: delivery start,
 * > each heading reached, pause, resume, stop and end; the trace view shown
 * > and hidden; questions started and finished. It records no text of his
 * > answers."
 *
 * Ten names, one per moment the ruling lists. Nothing else is a moment.
 *
 * Why this exists. The bug report of 16 October 2026 held seven events, the
 * last at 20:48:25, and was saved at 21:00:19: **eleven minutes and
 * fifty-four seconds of delivery with nothing recorded at all**, because
 * delivery logged nothing from the moment it started. His notes could not be
 * tied to the run, so a fault he described in a sentence had no record
 * beside it.
 */
export type SessionMomentName =
  | "delivery-end"
  | "delivery-pause"
  | "delivery-resume"
  | "delivery-start"
  | "delivery-stop"
  | "heading-reached"
  | "questions-finished"
  | "questions-started"
  | "trace-hidden"
  | "trace-shown";

/** A moment before it is timed. The log stamps it. */
export interface SessionMomentDraft {
  readonly detail: LogDetail;
  readonly name: SessionMomentName;
}

function isHeading(entry: ScheduledSpeech): boolean {
  return (
    entry.kind === "paragraph-heading" || entry.kind === "subparagraph-heading"
  );
}

/**
 * The moments one delivery state transition produced.
 *
 * Pure, so it can be tested rather than watched: the delivery pane calls it
 * from the one place it already learns of a transition, and the controller
 * keeps no knowledge of the log.
 *
 * `previous` is null on the controller's opening call, which reports the
 * state it was constructed with. That is the pane mounting, not the session
 * starting, so it yields nothing: **delivery starts when he presses play.**
 */
export function deliveryMoments(
  previous: DeliveryState | null,
  next: DeliveryState,
  schedule: readonly ScheduledSpeech[],
): readonly SessionMomentDraft[] {
  if (previous === null) return [];
  const moments: SessionMomentDraft[] = [];
  const begun =
    previous.status !== next.status &&
    next.status === "playing" &&
    previous.status !== "paused";
  if (previous.status !== next.status) {
    if (next.status === "playing") {
      // Resuming is not starting. A run that stopped and was played again is
      // a fresh start, which is why only "paused" resumes.
      moments.push({
        detail: {},
        name: begun ? "delivery-start" : "delivery-resume",
      });
    } else if (next.status === "paused") {
      moments.push({ detail: {}, name: "delivery-pause" });
    } else if (next.status === "complete") {
      moments.push({ detail: {}, name: "delivery-end" });
    } else {
      moments.push({ detail: {}, name: "delivery-stop" });
    }
  }
  const entry = schedule[next.cursor];
  /* v8 ignore next -- the state machine never leaves the cursor outside the schedule */
  if (entry === undefined) return moments;
  // A heading is reached by arriving at it, or by the run starting on the one
  // the cursor already rests on — which is how the first heading of every run
  // is reached, since pressing play moves no cursor.
  if ((previous.cursor !== next.cursor || begun) && isHeading(entry)) {
    moments.push({
      // Ruling 50: the node's path through the document, never its heading.
      detail: { kind: entry.kind, node: scheduleNodeId(entry.id) },
      name: "heading-reached",
    });
  }
  return moments;
}
